/**
 * Utility functions for communicating with the parent window
 */

// Send a message to the parent window
export function sendMessageToParent(message: any, targetOrigin = "*") {
  if (typeof window !== "undefined" && window.parent) {
    console.log(`Sending message to parent:`, message)
    window.parent.postMessage(message, targetOrigin)
  } else {
    console.warn("Cannot send message: window or parent not available")
  }
}

// Request a token from the parent window
export function requestTokenFromParent() {
  sendMessageToParent({ action: "REQUEST_TOKEN" })
}

// Request a new token due to authentication failure
export function requestTokenDueToAuthFailure(error?: string) {
  sendMessageToParent({
    action: "REQUEST_TOKEN",
    reason: "auth_failure",
    error: error,
  })
}

// Send navigation message to parent window
export function navigateInParent(route: string, options: { replaceUrl?: boolean; openInNewTab?: boolean; isInternalRoute?: boolean } = {}) {
  const { replaceUrl = false, openInNewTab = false, isInternalRoute = false } = options
  
  sendMessageToParent({
    action: "NAVIGATE",
    route: route,
    replaceUrl: replaceUrl,
    openInNewTab: openInNewTab,
    isInternalRoute: isInternalRoute,
  })
}

// Notify parent of internal route changes within this app
export function notifyParentOfRouteChange(path: string) {
  sendMessageToParent({
    action: "ROUTE_UPDATE",
    path: path,
  })
}

// Send title update to parent window
export function updateParentTitle(title: string) {
  sendMessageToParent({
    action: "UPDATE_TITLE",
    title: title,
  })
}

// Listen for messages from the parent window
export function listenToParentMessages(callback: (event: MessageEvent) => void) {
  if (typeof window !== "undefined") {
    window.addEventListener("message", callback)
    return () => window.removeEventListener("message", callback)
  }
  return () => {}
}

// Notify parent of dirty state changes
export function notifyParentOfDirtyState(isDirty: boolean) {
  sendMessageToParent({
    action: "SET_DIRTY_STATE",
    isDirty: isDirty,
  })
}

// Request navigation confirmation from parent
export function requestNavigationConfirmation() {
  sendMessageToParent({
    action: "REQUEST_NAVIGATION_CONFIRMATION",
  })
}

// The shell tells us its origin on the iframe URL, the same channel as culture. This app is served
// from its own origin, so nothing in a screen here can address the shell without it.
const PARENT_ORIGIN_PARAM = "parentOrigin"

function toHttpOrigin(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }
  try {
    const url = new URL(value)
    return url.protocol === "http:" || url.protocol === "https:" ? url.origin : null
  } catch {
    // Ignore anything that is not a URL.
    return null
  }
}

function readParentOrigin(): string | null {
  const fromQuery = typeof window === "undefined"
    ? null
    : toHttpOrigin(new URLSearchParams(window.location.search).get(PARENT_ORIGIN_PARAM))

  // A shell that predates the query parameter still leaves a referrer, except where a referrer
  // policy or a scheme downgrade strips it.
  return fromQuery ?? (typeof document === "undefined" ? null : toHttpOrigin(document.referrer))
}

// Read while the URL the shell gave us is still intact. This module is pulled in as the app starts
// up, well before its routing drops the query string, so there is nothing for a screen to catch in
// time and no start-up call to forget.
const capturedParentOrigin = readParentOrigin()

/**
 * The origin of the shell that embedded us, for building a URL a browser can act on (middle-click,
 * ctrl-click, "Open link in new tab"). Null when we are not embedded, or when the shell never said
 * where it lives, which leaves messaged navigation as the only way across.
 */
export function getParentOrigin(): string | null {
  if (typeof window === "undefined" || !window.parent || window.parent === window) {
    return null
  }
  return capturedParentOrigin
}
